import { useEffect, useState } from 'react'
import { Api } from '@/modules'
import { ClassificationTestItem } from '../../types'

interface UseClassificationTestPollingOptions {
  enabled?: boolean
  interval?: number
  companyID?: string
  pausePolling?: boolean
}

export function useClassificationTestPolling(
  initialTests: ClassificationTestItem[],
  options: UseClassificationTestPollingOptions = {},
) {
  const { enabled = true, interval = 10000, companyID, pausePolling = false } = options
  const [tests, setTests] = useState<ClassificationTestItem[]>(initialTests)

  const [syncAndList] = Api.PromiseQuery<{ tests: ClassificationTestItem[]; updated: number }>(
    `companies/:companyID/classification-tests/sync-and-list`,
  )

  const checkForUpdates = async (): Promise<void> => {
    if (!companyID) return

    try {
      const [response] = await syncAndList({ companyID })
      if (response) {
        setTests(response.tests)
      }
    } catch (error) {
      console.error('Erro ao buscar atualizações do backend:', error)
    }
  }

  const addTest = (newTest: ClassificationTestItem) => {
    setTests((prevTests) => [newTest, ...prevTests])
  }

  const updateTests = (newTests: ClassificationTestItem[]) => {
    setTests(newTests)
  }

  const forceCheckForUpdates = async (): Promise<void> => {
    if (!companyID) return

    try {
      const [response] = await syncAndList({ companyID })
      if (response) {
        setTests(response.tests)
      }
    } catch (error) {
      console.error('Erro ao buscar atualizações do backend:', error)
    }
  }

  useEffect(() => {
    if (!enabled || !companyID || pausePolling) return

    const hasProcessingTests = tests.some((test) => test.status === 'PROCESSING' || test.status === 'PENDING')

    if (!hasProcessingTests) {
      return
    }

    const intervalId = setInterval(checkForUpdates, interval)
    checkForUpdates()

    return () => {
      clearInterval(intervalId)
    }
  }, [tests, enabled, companyID, interval, pausePolling])

  useEffect(() => {
    setTests(initialTests)
  }, [initialTests])

  return {
    tests,
    addTest,
    updateTests,
    hasProcessingTests: tests.some((test) => test.status === 'PROCESSING' || test.status === 'PENDING'),
    processingCount: tests.filter((test) => test.status === 'PROCESSING' || test.status === 'PENDING').length,
    completedCount: tests.filter((test) => test.status === 'FINISHED').length,
    errorCount: tests.filter((test) => test.status === 'ERROR').length,
    checkForUpdates,
    forceCheckForUpdates,
  }
}
